import { useEffect, useState, type CSSProperties } from 'react';
import { BEATS } from '../content/beats';
import { missionOf } from '../content/missions';
import { chapterOf } from '../content/chapters';
import { STORY_LINES } from '../content/storyLines';
import { cheapestUnowned, itemById } from '../content/hq';
import { bandMastered } from '../engine/mastery';
import { chargeFill } from '../engine/fluency';
import { useGameStore } from '../store/gameStore';
import { loadAudio, play } from '../audio/player';
import { BeatView } from './BeatView';
import { StoryCard } from './StoryCard';
import { BaseView } from './BaseView';
import { SpeedRun } from './SpeedRun';
import { SpeedMeter } from './SpeedMeter';
import { CoPlayPrompt } from './CoPlayPrompt';
import { useCountUp } from './useCountUp';

/** Fixed bonus for doing a Speed Run — granted once per session, NOT scaled by speed
 *  (so there's no incentive to speed-tap; the real reward is "beat your best"). */
const SPEED_RUN_BONUS = 5;
/** Co-play "read to your grown-up" appears every Nth completed mission. */
const COPLAY_EVERY = 3;

type Overlay = 'base' | 'speedrun' | null;

/** Runs one engine-built session of beats; performance feeds the mastery model. */
export function StoryScreen({ onHome }: { onHome: () => void }) {
  const coins = useGameStore((s) => s.coins);
  const addCoins = useGameStore((s) => s.addCoins);
  const recordBeat = useGameStore((s) => s.recordBeat);
  const recordSpeedRun = useGameStore((s) => s.recordSpeedRun);
  const startSession = useGameStore((s) => s.startSession);
  const nextBeat = useGameStore((s) => s.nextBeat);
  const currentBeatIds = useGameStore((s) => s.currentBeatIds);
  const beatIndex = useGameStore((s) => s.beatIndex);
  const session = useGameStore((s) => s.session);
  const fluency = useGameStore((s) => s.fluency);
  const band = useGameStore((s) => s.band);
  const mastery = useGameStore((s) => s.mastery);
  const storySeen = useGameStore((s) => s.storySeen);
  const markIntroSeen = useGameStore((s) => s.markIntroSeen);
  const markVictorySeen = useGameStore((s) => s.markVictorySeen);

  const [earnedThisSession, setEarnedThisSession] = useState(0);
  const [overlay, setOverlay] = useState<Overlay>(null);
  const [bonusGivenSession, setBonusGivenSession] = useState(-1);
  const [coPlayDoneSession, setCoPlayDoneSession] = useState(-1);

  const beginSession = () => {
    setEarnedThisSession(0);
    setOverlay(null);
    startSession();
  };

  useEffect(() => {
    if (currentBeatIds.length === 0) beginSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBeatIds.length]);

  const beatId = currentBeatIds[beatIndex];
  const beat = beatId ? BEATS.find((b) => b.id === beatId) : undefined;

  const chapter = chapterOf(band);
  const sessionDone = currentBeatIds.length > 0 && beatIndex >= currentBeatIds.length;

  // Voiced comic spine — the chapter's intro card plays once before its first beat.
  if (chapter && !storySeen.intros.includes(band) && !sessionDone) {
    return <StoryCard kind="intro" chapter={chapter} onContinue={() => markIntroSeen(band)} />;
  }

  if (sessionDone) {
    // Beating a villain = mastering the whole level → its victory card plays once.
    if (chapter && bandMastered(mastery, band) && !storySeen.victories.includes(band)) {
      return <StoryCard kind="victory" chapter={chapter} onContinue={() => markVictorySeen(band)} />;
    }
    // Speed Run uses the session's first beat (a line he just read).
    const speedBeat = BEATS.find((b) => b.id === currentBeatIds[0]);
    const showCoPlay =
      session > 0 && session % COPLAY_EVERY === 0 && coPlayDoneSession !== session && overlay === null;

    return (
      <>
        <MissionComplete
          coins={coins}
          earned={earnedThisSession}
          recentWpm={fluency.recentWpm}
          canSpeedRun={!!speedBeat}
          onReplay={beginSession}
          onOpenBase={() => setOverlay('base')}
          onSpeedRun={() => setOverlay('speedrun')}
          onHome={onHome}
        />
        {overlay === 'base' && <BaseView onClose={() => setOverlay(null)} />}
        {overlay === 'speedrun' && speedBeat && (
          <SpeedRun
            beat={speedBeat}
            prevBestMs={fluency.bestMsByBeat[speedBeat.id]}
            onAttempt={(timeMs, wordCount) => {
              recordSpeedRun(speedBeat.id, timeMs, wordCount);
              if (bonusGivenSession !== session) {
                addCoins(SPEED_RUN_BONUS);
                setBonusGivenSession(session);
              }
            }}
            onClose={() => setOverlay(null)}
          />
        )}
        {showCoPlay && speedBeat && (
          <CoPlayPrompt line={correctedLine(speedBeat.id)} onDone={() => setCoPlayDoneSession(session)} />
        )}
      </>
    );
  }
  if (!beat) return null;

  return (
    <BeatView
      beat={beat}
      done={beatIndex}
      total={currentBeatIds.length}
      coins={coins}
      missionTitle={missionOf(beat.id)?.title}
      onComplete={(r, earned) => {
        addCoins(earned);
        setEarnedThisSession((e) => e + earned);
        recordBeat(beat.scramblePattern, r);
        nextBeat();
      }}
    />
  );
}

/** The corrected, readable sentence for a beat (broken word fixed). */
function correctedLine(beatId: string): string {
  const b = BEATS.find((x) => x.id === beatId);
  if (!b) return '';
  return b.words.map((w, i) => (i === b.brokenIndex ? b.correctWord : w)).join(' ') + '.';
}

function MissionComplete({
  coins,
  earned,
  recentWpm,
  canSpeedRun,
  onReplay,
  onOpenBase,
  onSpeedRun,
  onHome,
}: {
  coins: number;
  earned: number;
  recentWpm: number[];
  canSpeedRun: boolean;
  onReplay: () => void;
  onOpenBase: () => void;
  onSpeedRun: () => void;
  onHome: () => void;
}) {
  const owned = useGameStore((s) => s.owned);
  const featuredGoal = useGameStore((s) => s.featuredGoal);
  const shownCoins = useCountUp(coins, 1000, Math.max(0, coins - earned));

  const goalId = featuredGoal ?? cheapestUnowned(owned)?.id ?? null;
  const goal = goalId ? itemById(goalId) ?? null : null;
  const canBuild = goal ? coins >= goal.cost : false;
  const pct = goal ? Math.min(100, Math.round((coins / goal.cost) * 100)) : 100;

  const avgWpm = recentWpm.length ? recentWpm.reduce((a, b) => a + b, 0) / recentWpm.length : 0;

  useEffect(() => {
    void (async () => {
      await loadAudio();
      await play(STORY_LINES.complete.id);
    })();
  }, []);

  return (
    <div style={screen}>
      <div style={{ fontSize: 72 }}>🦸‍♂️🎉</div>
      <div style={{ fontFamily: 'var(--font-read)', fontSize: 'clamp(40px, 8vmin, 88px)', fontWeight: 800, color: 'var(--gold)' }}>
        Mission cleared!
      </div>
      <div style={{ fontSize: 26, color: '#f4f4f4' }}>
        🪙 {shownCoins} Hero Coins {earned > 0 && <span style={earnedTag}>+{earned}</span>}
      </div>

      {avgWpm > 0 && <SpeedMeter fill={chargeFill(avgWpm)} label="⚡ READING SPEED" />}

      {goal && (
        <div style={goalNudge}>
          <div style={goalNudgeText}>
            {canBuild ? `You can build the ${goal.name}! ${goal.art}` : `${goal.cost - coins} more to build the ${goal.name} ${goal.art}`}
          </div>
          <div style={miniTrack}>
            <div style={{ ...miniFill, width: `${pct}%` }} />
          </div>
        </div>
      )}

      <div style={btnRow}>
        {canSpeedRun && (
          <button type="button" style={speedBtn} onClick={onSpeedRun}>
            ⚡ Speed Run
          </button>
        )}
        <button type="button" style={baseCta} onClick={onOpenBase}>
          {canBuild ? 'Build it! 🏛️' : 'Your base 🏛️'}
        </button>
        <button type="button" style={homeCta} onClick={onHome}>
          🏠 Home
        </button>
        <button type="button" style={nextBtn} onClick={onReplay}>
          Next mission →
        </button>
      </div>
    </div>
  );
}

const screen: CSSProperties = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 16,
  padding: 20,
  background: 'var(--frame)',
};
const earnedTag: CSSProperties = { color: 'var(--good)', fontWeight: 800, marginLeft: 8 };
const goalNudge: CSSProperties = { width: 'min(440px, 80vw)', textAlign: 'center' };
const goalNudgeText: CSSProperties = { fontSize: 18, fontWeight: 700, color: '#cdd6ee', marginBottom: 8 };
const miniTrack: CSSProperties = { height: 12, borderRadius: 999, background: 'var(--frame-2)', overflow: 'hidden' };
const miniFill: CSSProperties = { height: '100%', background: 'var(--gold)', borderRadius: 999, transition: 'width 0.6s ease' };
const btnRow: CSSProperties = { display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center' };
const speedBtn: CSSProperties = {
  height: 'var(--target-min)',
  padding: '0 28px',
  borderRadius: 999,
  border: '2px solid var(--gold)',
  background: 'var(--gold)',
  color: 'var(--frame)',
  fontSize: 21,
  fontWeight: 800,
  cursor: 'pointer',
};
const baseCta: CSSProperties = {
  height: 'var(--target-min)',
  padding: '0 28px',
  borderRadius: 999,
  border: '2px solid var(--gold)',
  background: 'var(--frame-2)',
  color: 'var(--gold)',
  fontSize: 21,
  fontWeight: 800,
  cursor: 'pointer',
};
const homeCta: CSSProperties = {
  height: 'var(--target-min)',
  padding: '0 24px',
  borderRadius: 999,
  border: '2px solid var(--frame-edge)',
  background: 'var(--frame-2)',
  color: '#f4f4f4',
  fontSize: 20,
  fontWeight: 800,
  cursor: 'pointer',
};
const nextBtn: CSSProperties = {
  height: 'var(--target-min)',
  padding: '0 36px',
  borderRadius: 999,
  border: 'none',
  background: 'var(--spidey-red)',
  color: '#fff',
  fontSize: 23,
  fontWeight: 800,
  cursor: 'pointer',
};
